"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAssetApi = void 0;
const react_1 = require("@reduxjs/toolkit/query/react");
/**
 * Asset API factory — see createTicketApi.ts for the shared-package baseQuery-injection pattern.
 * `getAssets` (T108) backs the Preventive Maintenance section's asset lookup, matching web's own
 * `AssetPickerDialog` (`useGetAssetsQuery({ status: 'ACTV', center: centerId })`). `listAssetDocuments`
 * (T122) backs that same section's read-only Asset Documents drill-down, matching web's
 * `useListAssetDocumentsQuery`. `getAssetById` and `getAssetWorkOrderHistory` (T057-T058, User
 * Story 4) back the mobile equipment-scan lookup (`AssetDetailScreen`/`AssetWorkOrderHistoryScreen`)
 * — the latter hits the new, additive `GET Assets/{id}/work-orders` endpoint, distinct from the
 * cost summary and recurring-task reads. Legacy asset upserts, linked cost/PM reads, and document
 * mutations are shared endpoints so both clients use the same request contracts.
 */
const createAssetApi = (baseQuery) => (0, react_1.createApi)({
    reducerPath: 'assetApi',
    baseQuery,
    tagTypes: ['Asset', 'AssetList', 'Document'],
    endpoints: (builder) => ({
        getAssetById: builder.query({
            query: (id) => ({
                url: 'Asset',
                params: { id },
            }),
            transformResponse: (response, meta) => {
                var _a, _b;
                return (Object.assign(Object.assign({}, response), { etag: (_b = (_a = meta === null || meta === void 0 ? void 0 : meta.response) === null || _a === void 0 ? void 0 : _a.headers.get('ETag')) !== null && _b !== void 0 ? _b : undefined }));
            },
            providesTags: (_result, _error, id) => [{ type: 'Asset', id }],
        }),
        upsertAsset: builder.mutation({
            query: (_a) => {
                var { expectedEtag } = _a, body = __rest(_a, ["expectedEtag"]);
                return ({
                    url: 'Asset',
                    method: 'POST',
                    body: Object.assign(Object.assign({}, body), (expectedEtag !== undefined ? { expectedEtag } : {})),
                });
            },
            transformResponse: (response, meta) => {
                var _a, _b, _c;
                return (Object.assign(Object.assign({}, response), { etag: (_b = (_a = meta === null || meta === void 0 ? void 0 : meta.response) === null || _a === void 0 ? void 0 : _a.headers.get('ETag')) !== null && _b !== void 0 ? _b : undefined, merged: ((_c = meta === null || meta === void 0 ? void 0 : meta.response) === null || _c === void 0 ? void 0 : _c.headers.get('X-Asset-Merged')) === 'true' }));
            },
            invalidatesTags: (result, _error, arg) => {
                var _a, _b;
                return [
                    'AssetList',
                    { type: 'Asset', id: (_b = (_a = result === null || result === void 0 ? void 0 : result.id) !== null && _a !== void 0 ? _a : arg.assetView.id) !== null && _b !== void 0 ? _b : 'NEW' },
                ];
            },
        }),
        getAssets: builder.query({
            query: ({ status, center, category } = {}) => ({
                url: 'Assets',
                params: Object.assign(Object.assign(Object.assign({}, (status ? { status } : {})), (center ? { center } : {})), (category ? { category } : {})),
            }),
            providesTags: ['AssetList'],
        }),
        getAssetWorkOrderHistory: builder.query({
            query: (id) => ({
                url: `Assets/${id}/work-orders`,
            }),
            providesTags: (_result, _error, id) => [{ type: 'Asset', id }],
        }),
        listAssetDocuments: builder.query({
            query: (assetId) => ({
                url: `assets/${assetId}/documents`,
                method: 'GET',
            }),
            providesTags: (_result, _error, assetId) => [
                { type: 'Document', id: assetId },
                'Document',
            ],
        }),
        getAssetCosts: builder.query({
            query: (id) => `Assets/${id}/costs`,
            providesTags: (_result, _error, id) => [{ type: 'Asset', id }],
        }),
        getAssetRecurringTasksForMobile: builder.query({
            query: (assetId) => ({
                url: 'Assets/recurring-tasks',
                params: { assetId },
            }),
            providesTags: (_result, _error, id) => [{ type: 'Asset', id }],
        }),
        uploadAssetDocumentForMobile: builder.mutation({
            query: ({ assetId, file, fileName, documentType }) => {
                const formData = new FormData();
                formData.append('file', file, fileName);
                formData.append('documentType', documentType);
                return {
                    url: `assets/${assetId}/documents`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'Document', id: assetId }, 'Document'],
        }),
        deleteAssetDocumentForMobile: builder.mutation({
            query: ({ assetId, documentId }) => ({
                url: `assets/${assetId}/documents/${documentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'Document', id: assetId }, 'Document'],
        }),
    }),
});
exports.createAssetApi = createAssetApi;
